package ui.dashboard


import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridItemSpan
import androidx.compose.foundation.lazy.staggeredgrid.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.netguru.multiplatform.charts.pie.PieChart
import com.netguru.multiplatform.charts.pie.PieChartConfig
import common.frontend.api.IoReport
import compose.assets.Svg
import compose.assets.svg.Chevron_right
import org.jetbrains.compose.resources.painterResource
import androidx.compose.foundation.Image
import compose.assets.svg.Menu
import io.Repository
import org.jetbrains.compose.resources.StringResource
import org.ssoft.common.type.DashboardCategory
import org.ssoft.common.type.DashboardMsgType
import ssoft.frontend.generated.resources.*
import theme.Grey500
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.DataState
import tools.findRoot
import tools.rememberString
import ui.campaign.detailv2.preview.CampaignPreviewArgs
import ui.company.detail.CompanyArgs
import ui.device.detail.DeviceArgs
import ui.location.detail.LocationArgs
import ui.resource.ResourceListingArgs
import ui.widget.ErrUI
import ui.widget.LazyImage
import ui.widget.LoadingUI


data class ArgsDashboard(
    var serverId: Long = -1L,
    var companyId: Long = -1L,
    var menuEntry: Boolean = false
)

@ComposableNavNode(ArgsDashboard::class)
@Composable
fun DashboardUI() {
    with(DashboardScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}


@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun DashboardScope.Content(nnvl: DashboardScope.Wrapper) {
    Box(Modifier.fillMaxSize()) {
        LazyVerticalStaggeredGrid(
            columns = StaggeredGridCells.Adaptive(384.dp),
            verticalItemSpacing = 16.dp,
            horizontalArrangement = Arrangement.SpaceBetween,
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(16.dp)
        ) {
            if (display.isMobile && args.menuEntry) {
                item(span = StaggeredGridItemSpan.FullLine) {
                    Row(
                        Modifier.fillMaxWidth()
                            .clip(CardDefaults.shape)
                            .clickable { homeDrawerProvider.open() }) {
                        Repository.serverInfo?.imageLogoHorizontal?.also {
                            if (it == -29L) {
                                Image(
                                    painter = painterResource(Res.drawable.logo_panel_dark),
                                    contentDescription = null,
                                    contentScale = ContentScale.FillWidth,
                                    modifier = Modifier.fillMaxWidth()
                                        .padding(horizontal = 48.dp)
                                        .padding(16.dp)
                                )
                            } else {
                                LazyImage(
                                    id = it,
                                    contentScale = ContentScale.Inside,
                                    modifierPlaceHolder = Modifier.fillMaxWidth()
                                        .padding(horizontal = 48.dp)
                                        .padding(16.dp),
                                    modifierContent = Modifier.fillMaxWidth()
                                )
                            }
                        }
                    }
                }
            }
            items(nnvl.state) {

                when (it.data) {
                    is DashboardScope.Chart -> Item(it.data)
                    is DashboardScope.Message -> Item(it.data)
                }

            }
        }
        if (display.isMobile && args.menuEntry) {

            val infiniteTransition = rememberInfiniteTransition(label = "pulsing_color_transition")

            // 2. Define the animated color.
            //    It animates from the initial value to the target value and then reverses.
            val animatedColor by infiniteTransition.animateColor(
                initialValue = Color.White, // The color at the start of the animation
                targetValue = Color.Grey500, // The color at the end of the animation
                animationSpec = infiniteRepeatable(
                    // The duration of one animation cycle (e.g., from Red to Blue).
                    animation = tween(durationMillis = 2000),
                    // The repeat mode. Reverse makes it go from Red to Blue, then Blue to Red.
                    repeatMode = RepeatMode.Reverse
                ),
                label = "pulsing_color"
            )
            Icon(
                imageVector = Svg.Menu,
                contentDescription = null,
                modifier = Modifier
                    .size(48.dp)
                    .background(animatedColor, RoundedCornerShape(0, 0, 50, 0))
                    .clip(RoundedCornerShape(0, 0, 50, 0))
                    .clickable { homeDrawerProvider.open() }
                    .padding(12.dp)
                    .padding(end = 2.dp, bottom = 2.dp),
                tint = Color.Black
            )
        }
    }

}

@Composable
private fun DashboardScope.Item(item: DashboardScope.Message) {
    Box(Modifier.fillMaxWidth()) {
        Card(
            modifier = Modifier.align(Alignment.Center),
            colors = CardDefaults.cardColors(MaterialTheme.colorScheme.surfaceContainer)
        ) {
            Column(
                modifier = Modifier.width(384.dp)
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Icon(
                        item.icon,
                        null,
                        modifier = Modifier.size(32.dp)
                            .align(Alignment.CenterVertically)
                    )
                    Text(
                        text = item.title.rememberString(),
                        style = MaterialTheme.typography.headlineSmall,
                        modifier = Modifier.align(Alignment.CenterVertically)

                    )
                }
                if (item.messages.isEmpty()) Text(Res.string.dashboard__problems_none.rememberString())
                else {
                    item.messages
                        .groupBy { it.type }
                        .forEach { MessageEntry(DashboardMsgType[it.key], it.value) }

                }
            }
        }
    }
}


@Composable
private fun DashboardScope.MessageEntry(subtype: DashboardMsgType, items: List<IoReport.Dashboard.Message>) {
    var expanded by remember {
        mutableStateOf(false)
    }
    val animatedRotation by animateFloatAsState(
        if (expanded) 90F else 0F
    )

    Card(
        colors = CardDefaults.cardColors(MaterialTheme.colorScheme.surfaceContainerHigh)
    ) {
        Row(
            Modifier.clip(CardDefaults.shape)
                .clickable { expanded = !expanded }
                .padding(4.dp)
                .padding(start = 4.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {

            Text(
                text = subtype.stringRes().rememberString(items.size),
                style = MaterialTheme.typography.labelMedium,
                modifier = Modifier.weight(1F)
                    .align(Alignment.CenterVertically)
            )
            Icon(
                imageVector = Svg.Chevron_right,
                contentDescription = null,
                modifier = Modifier.align(Alignment.CenterVertically)
                    .size(18.dp)
                    .rotate(animatedRotation)
            )
        }
        AnimatedVisibility(
            visible = expanded,
            enter = fadeIn() + expandVertically(),
            exit = fadeOut() + shrinkVertically()
        ) {
            Column(
                modifier = Modifier.fillMaxWidth()
                    .padding(bottom = 4.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                items.forEach { entry ->
                    ElevatedCard(
                        modifier = Modifier.padding(horizontal = 4.dp)
                            .fillMaxWidth(),
                        colors = CardDefaults.elevatedCardColors(
                            containerColor = MaterialTheme.colorScheme.surfaceContainer
                        ),
                        onClick = {
                            when (subtype.category) {
                                DashboardCategory.CAMPAIGN -> nav.findRoot().navTo_CampaignPreviewUI(
                                    CampaignPreviewArgs(
                                        companyId = entry.companyId,
                                        campaignGroupId = entry.campaignGroupId
                                    )
                                )

                                DashboardCategory.DEVICE -> nav.findRoot().navTo_DevicePreviewUI(
                                    DeviceArgs(deviceId = entry.deviceId)
                                )

                                DashboardCategory.LOCATION -> nav.findRoot().navTo_LocationUI(
                                    LocationArgs(
                                        companyId = entry.companyId,
                                        locationId = entry.locationId
                                    )
                                )

                                DashboardCategory.COMPANY -> {
                                    if (subtype == DashboardMsgType.LOW_STORAGE_MEDIA) {
                                        nav.findRoot().navTo_ResourceListingUI(
                                            ResourceListingArgs(
                                                companyId = entry.companyId
                                            )
                                        )
                                    } else {
                                        nav.findRoot().navTo_CompanyUI(
                                            CompanyArgs(
                                                companyId = entry.companyId,
                                                forceUpNavigation = true
                                            )
                                        )
                                    }
                                }

                                DashboardCategory.UNDEFINED -> Unit
                            }
                        }
                    ) {
                        Column(
                            Modifier.padding(4.dp)
                                .padding(start = 4.dp)
                        ) {
                            Text(
                                text = entry.name1,
                                maxLines = 1,
                                style = MaterialTheme.typography.bodyMedium,
                                overflow = TextOverflow.Ellipsis
                            )
                            Text(
                                text = entry.name2,
                                maxLines = 1,
                                style = MaterialTheme.typography.labelSmall,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DashboardScope.Item(item: DashboardScope.Chart) {
    val chartData = remember {
        item.fields.filter { it.showInChart }.map { it.toPieChartData() }
    }
    Box(Modifier.fillMaxWidth()) {
        Card(
            modifier = Modifier.align(Alignment.Center),
            colors = CardDefaults.cardColors(MaterialTheme.colorScheme.surfaceContainer)
        ) {
            Column(
                modifier = Modifier.width(384.dp)
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {

                Row {
                    Column(Modifier.weight(1F).align(Alignment.CenterVertically)) {
                        Text(
                            text = item.title.rememberString(),
                            style = MaterialTheme.typography.headlineMedium
                        )
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Icon(item.icon, null, Modifier.align(Alignment.CenterVertically))

                            Text(
                                text = item.subtitle.rememberString(),
                                style = MaterialTheme.typography.headlineSmall
                            )
                        }
                    }
                    PieChart(
                        data = chartData,
                        config = PieChartConfig(
                            thickness = 16.dp,
                            gap = 4.dp
                        ),
                        modifier = Modifier.size(96.dp).align(Alignment.CenterVertically)
                    )
                }

                item.fields.forEach {
                    Column {
                        Row {
                            Text(
                                text = it.label.rememberString(),
                                style = MaterialTheme.typography.bodyMedium,
                                modifier = Modifier.weight(1F)
                            )
                            Text(
                                text = it.valuePreview,
                                fontWeight = FontWeight.Bold
                            )
                        }
                        Spacer(
                            Modifier.fillMaxWidth()
                                .height(4.dp)
                                .background(it.color, RoundedCornerShape(50))
                        )
                    }
                }
            }
        }
    }
}

private fun DashboardMsgType.stringRes(): StringResource = when (this) {
    DashboardMsgType.CAMPAIGN_FULLFILMENT -> Res.string.dashboard__campaign_msg_field_fulfillment
    DashboardMsgType.CAMPAIGN_NO_DEVICES -> Res.string.dashboard__campaign_msg_field_no_devices
    DashboardMsgType.CAMPAIGN_NO_IMPRESSIONS_LIVETIME -> Res.string.dashboard__campaign_msg_field_impressions_lifetime
    DashboardMsgType.CAMPAIGN_NO_IMPRESSIONS_TODAY -> Res.string.dashboard__campaign_msg_field_impressions_today
    DashboardMsgType.CAMPAIGN_NO_IMPRESSIONS_TODAY_ONE_OR_MORE_DEVICES -> Res.string.dashboard__campaign_msg_field_impressions_device_one_or_more
    DashboardMsgType.CAMPAIGN_WITHOUT_IMPRESSIONS -> Res.string.dashboard__device_msg_field_impressions_no
    DashboardMsgType.DISCONNECTED -> Res.string.dashboard__device_msg_field_disconnected
    DashboardMsgType.DISPLAY_OFF -> Res.string.dashboard__device_msg_field_display_off
    DashboardMsgType.EMERGENCY_ACTIVE -> Res.string.dashboard__location_msg_field_emergency_active
    DashboardMsgType.LOW_STORAGE_DEVICE -> Res.string.dashboard__device_msg_field_low_storage
    DashboardMsgType.LOW_STORAGE_MEDIA -> Res.string.dashboard__media_msg_field_low_storage
    DashboardMsgType.NO_APPS -> Res.string.dashboard__device_msg_field_no_apps
    DashboardMsgType.NO_CAMPAIGNS -> Res.string.dashboard__device_msg_field_no_campaign
    DashboardMsgType.OVERHEATING -> Res.string.dashboard__device_msg_field_overheating
    DashboardMsgType.UNDEFINED -> Res.string.global_empty
    DashboardMsgType.UPDATE_REQUIRED -> Res.string.dashboard__device_msg_field_update_required
}